import React, { createContext, Dispatch, ReactElement, useContext, useEffect, useReducer } from 'react'
import errors from '../../App/errors';
import { PageContext } from '../../App/PageContextContainer';
import loadingNotifications from '../../App/loadingNotifications';
import { IAppliedFilters } from '../../App/AppSettingsContextContainer';
import { IItemWithLanguageOptions } from '../../components/TranslatedOption/TranslatedOption';

type Props = {
    children: ReactElement
}

export const ObservationsContext = createContext({} as IObservationsContext);

type ContextAction =
    | { type: 'setAllTaxons', taxons: ITaxon[] }
    | { type: 'setAllCollections', collections: ICollection[] }
    | {
        type: 'setAllSearchCriteria', collectionIds: string[], taxonIds: string[], declarationArrivalMonths: number[],
        observationMonths: number[], cuttingRealizationPractices: string[]
    }
    | { type: "setInitialized" }
    | { type: "setClashesToForget", clashes: IClashToForget[] }
    | { type: "removeClashToForget", id: string }
    | { type: "initialiseClashesToForgetFromStorage", clashes: IClashToForget[] }
    | { type: "setForgetAllVisibleClashesHandler", handler: () => void }
    | { type: "forgetAllVisibleClashes" }
    | { type: "reset" };

function contextReducer(state: IObservationsState, action: ContextAction): IObservationsState {
    switch (action.type) {
        case "setInitialized":
            return { ...state, isInitialized: true };
        case "setAllCollections":
            return { ...state, allCollections: action.collections };
        case "setAllTaxons":
            return { ...state, allTaxons: action.taxons };
        case 'setAllSearchCriteria':
            localStorage.setItem("appliedObservationFilters", JSON.stringify(action));

            return {
                ...state, taxonIds: action.taxonIds, collectionIds: action.collectionIds, observationMonths: action.observationMonths,
                declarationArrivalMonths: action.declarationArrivalMonths, cuttingRealizationPractices: action.cuttingRealizationPractices
            }
        case "initialiseClashesToForgetFromStorage":
            return {
                ...state, clashesToForget: action.clashes
            }
        case "setClashesToForget":
            const addToLocalStorage = () => {
                const data: string | null = localStorage.getItem('items');
                const items: IClashToForget[] = JSON.parse(data || "[]");

                for (const clash of action.clashes) {
                    const indexOfExistingItem = items.findIndex(existingItem => existingItem.id === clash.id);

                    if (indexOfExistingItem < 0) {
                        items.push(clash);
                    }
                }

                localStorage.setItem('items', JSON.stringify(items));

                return items;
            }

            const itemsAfterAdding = addToLocalStorage();

            return {
                ...state, clashesToForget: itemsAfterAdding
            }
        case "removeClashToForget":
            const removeFromLocalStorage = () => {
                const data: string | null = localStorage.getItem('items');
                const items: IClashToForget[] = JSON.parse(data || "[]");
                const indexOfItemToRemove = items.findIndex(existingClash => existingClash.id === action.id);

                if (indexOfItemToRemove >= 0) {
                    items.splice(indexOfItemToRemove, 1);
                }

                localStorage.setItem('items', JSON.stringify(items));

                return items;
            }

            const data = removeFromLocalStorage();

            return {
                ...state, clashesToForget: data
            }
        case "setForgetAllVisibleClashesHandler":
            return { ...state, forgetAllVisibleClashesHandler: action.handler };
        case "forgetAllVisibleClashes":
            if (state.forgetAllVisibleClashesHandler) {
                state.forgetAllVisibleClashesHandler();
            }

            return { ...state };
        case 'reset':
            localStorage.removeItem("appliedObservationFilters");

            return { ...initialState, isInitialized: true, allCollections: state.allCollections, allTaxons: state.allTaxons, clashesToForget: state.clashesToForget };
        default:
            throw new Error();
    }
}

export interface IObservationsContext {
    observationsStateDispatch: Dispatch<ContextAction>,
    observationsState: IObservationsState
}

export interface ITaxon extends IItemWithLanguageOptions {
    id: string
}

export interface ICollection extends IItemWithLanguageOptions {
    id: string
}

export interface IClashToForget {
    id: string,
    unitId: string,
    forgetDate: string,
    declarationArrivalDate: string
}

export interface IObservationsState {
    isInitialized: boolean,
    taxonIds: string[],
    collectionIds: string[],
    observationMonths: number[],
    cuttingRealizationPractices: string[],
    allTaxons?: ITaxon[],
    allCollections?: ICollection[],
    declarationArrivalMonths: number[],
    clashesToForget?: IClashToForget[],
    forgetAllVisibleClashesHandler?: () => void
}

const initialState = {
    isInitialized: false, taxonIds: [], collectionIds: [], cuttingRealizationPractices: [],
    observationMonths: [], declarationArrivalMonths: []
} as IObservationsState;

export default function ObservationsContextContainer(props: Props) {
    const [observationsState, observationsStateDispatch] = useReducer(contextReducer, initialState);
    const pageContext = useContext(PageContext);

    useEffect(() => {
        const asyncCall = async () => {
            try {
                pageContext.setLoadingSomething(loadingNotifications.loadingCollectionMaps);

                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/GetCollectionAndTaxonMapsV2`);

                const collectionsAndTaxons = await response.json();

                observationsStateDispatch({ type: "setAllCollections", collections: collectionsAndTaxons.collections });
                observationsStateDispatch({ type: "setAllTaxons", taxons: collectionsAndTaxons.taxons });
            } catch (error) {
                pageContext.setMapError(errors.taxonAndCollectionMappingsDataLoadingError);
            }
            finally {
                pageContext.resetLoadingSomething(loadingNotifications.loadingCollectionMaps);
            }

            const data: string | null = localStorage.getItem('items');
            const items: IClashToForget[] = JSON.parse(data || "[]");

            observationsStateDispatch({ type: "initialiseClashesToForgetFromStorage", clashes: items });

            const filtersFromLocalStorage = localStorage.getItem('appliedFilters');
            const customFiltersFromLocalStorage = localStorage.getItem('appliedObservationFilters');

            if (filtersFromLocalStorage || customFiltersFromLocalStorage) {
                const appliedFilters: IAppliedFilters = JSON.parse(filtersFromLocalStorage || "{}");
                const customAppliedFilters: IObservationsState = JSON.parse(customFiltersFromLocalStorage || "{}");

                observationsStateDispatch({
                    type: 'setAllSearchCriteria',
                    collectionIds: customAppliedFilters.collectionIds || [],
                    taxonIds: customAppliedFilters.taxonIds || [],
                    declarationArrivalMonths: appliedFilters.declarationArrivalMonths || [],
                    observationMonths: customAppliedFilters.observationMonths || [],
                    cuttingRealizationPractices: appliedFilters.cuttingRealizationPractices || []
                });
            }

            observationsStateDispatch({ type: "setInitialized" });
        }

        asyncCall();
    }, []);

    return (
        <ObservationsContext.Provider value={{ observationsStateDispatch, observationsState }}>
            {props.children}
        </ObservationsContext.Provider>
    )
}